import type { Dayjs } from 'dayjs';
import { getPrecedingComparisonRange } from 'app/modules/analytics/utils/getPrecedingComparisonRange';
import {
  AnalyticsDateRange,
  fitRangeAboveAnalyticsMinDate,
} from 'app/modules/analytics/utils/analyticsDateLimit';

export enum ComparisonPreset {
  PREVIOUS_PERIOD = 'previous_period',
  PREVIOUS_YEAR = 'previous_year',
}

export const COMPARISON_PRESET_ORDER: ComparisonPreset[] = [
  ComparisonPreset.PREVIOUS_PERIOD,
  ComparisonPreset.PREVIOUS_YEAR,
];

export const COMPARISON_PRESET_LABEL_KEYS: Record<ComparisonPreset, string> = {
  [ComparisonPreset.PREVIOUS_PERIOD]: 'analyticsComparePreviousPeriod',
  [ComparisonPreset.PREVIOUS_YEAR]: 'analyticsComparePreviousYear',
};

export function isComparisonPreset(
  value: string | null | undefined,
): value is ComparisonPreset {
  return (
    value === ComparisonPreset.PREVIOUS_PERIOD ||
    value === ComparisonPreset.PREVIOUS_YEAR
  );
}

/**
 * Resolves a comparison range spanning exactly as many days as the main period.
 *
 * Returns `null` when the history floor leaves no room for an equal-length
 * range before the main period starts.
 */
export function resolveComparisonRange(
  preset: ComparisonPreset,
  startDate: Dayjs,
  endDate: Dayjs,
): AnalyticsDateRange | null {
  if (preset === ComparisonPreset.PREVIOUS_YEAR) {
    const offsetToEnd = endDate.diff(startDate, 'day');
    const comparisonStartDate = startDate.subtract(1, 'year');

    return fitRangeAboveAnalyticsMinDate(
      {
        startDate: comparisonStartDate,
        endDate: comparisonStartDate.add(offsetToEnd, 'day'),
      },
      startDate,
    );
  }

  const preceding = getPrecedingComparisonRange(startDate, endDate);

  return fitRangeAboveAnalyticsMinDate(
    {
      startDate: preceding.comparisonStartDate,
      endDate: preceding.comparisonEndDate,
    },
    startDate,
  );
}
